import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface CompactStatCardProps {
    title: string;
    value: string | number;
    icon?: LucideIcon;
    trend?: {
        value: number;
        label?: string;
    };
    className?: string;
    variant?: 'default' | 'success' | 'warning' | 'danger';
}

/**
 * CompactStatCard - A mobile-optimized stat card with reduced padding.
 * 
 * Use instead of the standard Card + CardContent pattern for stats on mobile.
 * Designed for 360px viewport with 2-column grid layout.
 */
export const CompactStatCard: React.FC<CompactStatCardProps> = ({
    title,
    value,
    icon: Icon,
    trend,
    className,
    variant = 'default',
}) => {
    const variantStyles = {
        default: 'bg-card border',
        success: 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900',
        warning: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-900',
        danger: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900',
    };

    const iconVariantStyles = {
        default: 'text-muted-foreground',
        success: 'text-green-600',
        warning: 'text-yellow-600',
        danger: 'text-red-600',
    };

    const TrendIcon = trend
        ? trend.value > 0
            ? TrendingUp
            : trend.value < 0
                ? TrendingDown
                : Minus
        : null;

    const trendColor = trend
        ? trend.value > 0
            ? 'text-green-600'
            : trend.value < 0
                ? 'text-red-600'
                : 'text-muted-foreground'
        : '';

    return (
        <div
            className={cn(
                "rounded-lg p-2.5 md:p-4",
                variantStyles[variant],
                className
            )}
        >
            <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] md:text-xs uppercase tracking-wider text-muted-foreground truncate">
                    {title}
                </p>
                {Icon && (
                    <Icon className={cn("h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0", iconVariantStyles[variant])} />
                )}
            </div>
            <p className="text-lg md:text-2xl font-bold mt-1 truncate">
                {value}
            </p>
            {trend && TrendIcon && (
                <div className={cn("flex items-center gap-1 mt-1", trendColor)}>
                    <TrendIcon className="h-3 w-3" />
                    <span className="text-[10px] md:text-xs font-medium">
                        {trend.value > 0 ? '+' : ''}{trend.value}%
                        {trend.label && <span className="text-muted-foreground ml-1">{trend.label}</span>}
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * CompactStatGrid - A responsive grid for CompactStatCards
 */
interface CompactStatGridProps {
    children: React.ReactNode;
    columns?: 2 | 3 | 4;
    className?: string;
}

export const CompactStatGrid: React.FC<CompactStatGridProps> = ({
    children,
    columns = 2,
    className,
}) => {
    const gridCols = {
        2: 'grid-cols-2',
        3: 'grid-cols-2 md:grid-cols-3',
        4: 'grid-cols-2 md:grid-cols-4',
    };

    return (
        <div className={cn("grid gap-2 md:gap-4", gridCols[columns], className)}>
            {children}
        </div>
    );
};

export default CompactStatCard;
